/* ExchangeWeekProtocol.jsx — contextual final-week operational view.
 *
 * Appears at the top of the Deal Room when the transaction is in
 * its final 7 days to exchange (or has just exchanged). This is
 * NOT a new tab, NOT a new page, NOT a dashboard. It is a calm
 * critical-path ribbon that activates contextually and recedes
 * when the deal is outside the exchange window.
 *
 * Doctrine:
 *   - Single horizontal strip / stacked card row.
 *   - Operational vocabulary only.
 *   - Calm by default; never alarm even when overdue.
 *   - Inherits the locked card grammar (border-slate-100,
 *     shadow-[rgba(15,23,42,0.02)], cf-card-hover).
 *   - All status derivation is deterministic — same engine
 *     primitives as Exchange Readiness.
 */
import { useMemo } from "react";
import {
  CheckCircle2, Clock, Banknote, FileSignature, Key, ScrollText,
  CalendarCheck, Users,
} from "lucide-react";
import { computeExchangeReadiness } from "../../lib/exchangeReadiness";

/* Canonical 6-step critical path for the final exchange week.
 * Order = operational sequence agents follow in the last 7 days. */
const PROTOCOL_STEPS = [
  {
    id: "signed_contracts",
    label: "Signed contracts received",
    Icon: FileSignature,
    requires: ["contracts_ready"],          // readiness milestones
    sub: "Buyer + seller signed",
  },
  {
    id: "completion_date",
    label: "Completion date agreed",
    Icon: CalendarCheck,
    requires: ["exchange_date_agreed"],
    sub: "Confirmed by both solicitors",
  },
  {
    id: "funds_confirmed",
    label: "Buyer funds in place",
    Icon: Banknote,
    requires: ["mortgage_offer"],
    sub: "Mortgage + deposit cleared",
  },
  {
    id: "parties_aligned",
    label: "Parties aligned",
    Icon: Users,
    requires: ["solicitor_instructed"],
    sub: "Buyer · seller · solicitors briefed",
  },
  {
    id: "enquiries_clear",
    label: "Enquiries resolved",
    Icon: ScrollText,
    requires: ["enquiries_resolved"],
    sub: "No outstanding legal queries",
  },
  {
    id: "exchange_day",
    label: "Exchange day arranged",
    Icon: Key,
    requires: [],                            // always outstanding until terminal
    sub: "Time + protocol confirmed",
  },
];

function _stepStatus(step, readiness, isTerminal) {
  if (isTerminal) return "complete";
  if (step.requires.length === 0) return "outstanding";
  const completedIds = new Set(readiness.completed.map((m) => m.id));
  const allDone = step.requires.every((r) => completedIds.has(r));
  return allDone ? "complete" : "outstanding";
}

/* Active window: 7 days before target exchange through 1 day
 * after. Outside this window the banner does not render. */
export function isExchangeWeekActive(daysToExchange) {
  if (daysToExchange == null) return false;
  return daysToExchange >= -1 && daysToExchange <= 7;
}

export function ExchangeWeekProtocol({ deal, daysToExchange, targetExchangeDate, activity = [], testid = "exchange-week-protocol" }) {
  const active = isExchangeWeekActive(daysToExchange);
  const readiness = useMemo(
    () => computeExchangeReadiness(deal, activity),
    [deal, activity]
  );
  const isTerminal = readiness.state === "Exchanged";
  const steps = useMemo(
    () => PROTOCOL_STEPS.map((s) => ({ ...s, status: _stepStatus(s, readiness, isTerminal) })),
    [readiness, isTerminal]
  );

  if (!active) return null;

  const completedCount = steps.filter((s) => s.status === "complete").length;
  const total = steps.length;

  // Headline phrasing — calm by default; only nudges to urgency
  // when overdue, never alarms.
  let headline;
  if (daysToExchange < 0)       headline = `Exchange day passed · ${Math.abs(daysToExchange)} day overdue`;
  else if (daysToExchange === 0) headline = `Exchange day · today`;
  else if (daysToExchange === 1) headline = `Exchange day · tomorrow`;
  else                          headline = `Exchange in ${daysToExchange} days`;

  const headlineTone =
    daysToExchange < 0   ? "#92400E" :
    daysToExchange <= 2  ? "#1E293B" :
                           "#1E293B";

  return (
    <section
      className="mt-4 rounded-2xl bg-white border border-slate-100 p-5 md:p-6"
      style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.02)" }}
      data-testid={testid}
      aria-label="Exchange-week operational protocol"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4 md:mb-5">
        <div className="min-w-0">
          <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-slate-500">
            Exchange-week protocol
          </p>
          <div className="flex items-baseline gap-2.5 mt-1.5 flex-wrap">
            <span
              className="text-[20px] font-bold tracking-tight tabular-nums"
              style={{ color: headlineTone }}
              data-testid={`${testid}-headline`}
            >
              {headline}
            </span>
            {targetExchangeDate && (
              <span className="text-[12px] text-slate-500 tabular-nums">
                · target {targetExchangeDate}
              </span>
            )}
          </div>
        </div>
        <span
          className="inline-flex items-center justify-center min-w-[44px] h-7 px-2 rounded-md text-[11px] font-bold tabular-nums shrink-0"
          style={{ background: "#F1F5F9", color: "#475569" }}
          data-testid={`${testid}-count`}
        >
          {completedCount} / {total}
        </span>
      </div>

      {/* Critical-path strip — horizontal on desktop, stacked on mobile */}
      <ol
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5"
        data-testid={`${testid}-steps`}
      >
        {steps.map((step) => {
          const done = step.status === "complete";
          return (
            <li
              key={step.id}
              className="flex items-start gap-3 rounded-xl border border-slate-100 px-3.5 py-3 cf-card-hover"
              data-testid={`${testid}-step-${step.id}`}
              data-status={step.status}
            >
              <span
                className="w-7 h-7 rounded-lg inline-flex items-center justify-center shrink-0"
                style={{
                  background: done ? "rgba(16,185,129,0.10)" : "#F8FAFC",
                  color: done ? "#059669" : "#94A3B8",
                  border: done ? "none" : "1px solid #E2E8F0",
                }}
                aria-hidden
              >
                {done ? <CheckCircle2 size={14} strokeWidth={2.25} /> : <step.Icon size={13} strokeWidth={2} />}
              </span>
              <div className="min-w-0">
                <p
                  className={`text-[12.5px] font-semibold leading-tight ${done ? "text-slate-900" : "text-slate-700"}`}
                >
                  {step.label}
                </p>
                <p className="text-[11.5px] text-slate-500 mt-0.5 leading-snug">
                  {step.sub}
                </p>
              </div>
              {done && (
                <span className="ml-auto shrink-0 text-[10px] uppercase tracking-[0.16em] font-bold text-emerald-600 mt-0.5">
                  Done
                </span>
              )}
              {!done && (
                <span className="ml-auto shrink-0 text-[10px] uppercase tracking-[0.16em] font-bold text-slate-400 mt-0.5 inline-flex items-center gap-1">
                  <Clock size={9} strokeWidth={2.5} />
                  Open
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
